/**
 * Button Component
 *
 * Abstraction layer over MUI Button.
 * Usage: Import from '@/components/ui' instead of '@mui/material'
 *
 * Behavioural override: every primary `contained` button gets the
 * brand gradient (`gradientPrimaryButtonSx`) automatically. The gradient
 * derives from the theme's `primary` palette, so it tracks the tenant's
 * brand color rather than a hardcoded one. Centralising it here makes
 * every Save / Submit / Confirm / Sign-in CTA read identically without
 * the caller having to spread `gradientPrimaryButtonSx` by hand.
 *
 * The gate is conservative: it only fires for `variant="contained"`
 * AND the default-or-primary color. Other variants (outlined / text /
 * inherit) and other colors (error / success / secondary) are
 * untouched, so Cancel buttons (`color="inherit"`) and destructive
 * actions (`color="error"`) keep their existing chrome.
 *
 * The gradient is applied as the FIRST entry in the sx array so any
 * caller-provided `sx` still wins on a per-key basis — sizing,
 * spacing, border radius, font size etc. all override cleanly.
 */

import { ButtonProps, Button as MuiButton, SxProps, Theme } from '@mui/material';
import React from 'react';
import { gradientPrimaryButtonSx } from '../../utils/brandStyles';

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, color, sx, ...rest }, ref) => {
    // Default MUI Button `color` is 'primary' when not specified, so
    // both undefined and 'primary' qualify here.
    const isPrimaryContained = variant === 'contained' && (!color || color === 'primary');

    const mergedSx: SxProps<Theme> | undefined = isPrimaryContained
      ? Array.isArray(sx)
        ? [gradientPrimaryButtonSx, ...sx]
        : sx
          ? [gradientPrimaryButtonSx, sx as SxProps<Theme>]
          : [gradientPrimaryButtonSx]
      : sx;

    return <MuiButton ref={ref} variant={variant} color={color} sx={mergedSx} {...rest} />;
  },
);

Button.displayName = 'Button';

export default Button;
